import { useNuxtApp, useRuntimeConfig } from '#app'
import { subscribeApi } from '@/api'

class OneSignalSubscriptionService {
  constructor(nuxtApp, config, { dtVerify, uidSite }) {
    this.app = nuxtApp
    this.config = config
    this.dtVerify = dtVerify
    this.uidSite = uidSite
    this.cleanupSubscriptionListener = null
    this.dt = null
    this.isInitialized = false
    this.sdkInitPromise = null
    this.setupPromise = null
    this.pendingSyncPromise = null
    this.teardownPromise = null
    this.activeSetupKey = null
    this.lifecycleId = 0
  }

  get oneSignal() {
    return this.app.$OneSignal
  }

  async setup({ dt } = {}) {
    const dtVerify = this.dtVerify.value
    const uidSite = this.uidSite.value

    if (!uidSite) return { status: 'skipped', reason: 'no_uid_site' }
    if (this.app.$bridge?.available) return { status: 'skipped', reason: 'native_bridge' }
    if (dtVerify && !dt) {
      console.warn('[OneSignal] setup skipped: dtVerify enabled but no device token')
      return { status: 'skipped', reason: 'no_dt' }
    }
    if (!this.config.oneSignal) return { status: 'skipped', reason: 'missing_app_id' }

    const options = { dtVerify, dt, uidSite }

    const teardownPromise = this.teardownPromise
    if (teardownPromise) {
      const lifecycleId = this.lifecycleId
      await teardownPromise
      if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
    }

    const setupKey = options.dtVerify ? `dt:${options.dt}` : `uid:${options.uidSite}`
    if (setupKey === this.activeSetupKey && this.setupPromise) return await this.setupPromise
    if (setupKey === this.activeSetupKey) return { status: 'success', reason: 'already_setup' }

    const previousSetupPromise = this.setupPromise
    this.removeSubscriptionListener()
    this.dt = null
    this.activeSetupKey = setupKey
    const lifecycleId = ++this.lifecycleId
    const setupPromise = Promise.resolve()
      .then(async () => {
        if (previousSetupPromise) await previousSetupPromise
        if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
        const result = await this.setupOnce(options, lifecycleId)
        if (result.status === 'failed' && this.isCurrentLifecycle(lifecycleId) && this.activeSetupKey === setupKey) this.activeSetupKey = null
        return result
      })
      .catch((error) => {
        console.warn('[OneSignal] setup failed', error)
        if (this.isCurrentLifecycle(lifecycleId) && this.activeSetupKey === setupKey) this.activeSetupKey = null
        return { status: 'failed', reason: 'setup_failed' }
      })
      .finally(() => {
        if (this.setupPromise === setupPromise) this.setupPromise = null
      })

    this.setupPromise = setupPromise
    return await setupPromise
  }

  async setupOnce({ dtVerify, dt, uidSite }, lifecycleId) {
    if (!dtVerify) {
      await this.initSdk({ autoPrompt: true }, lifecycleId)
      if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
      const subscriptionListener = await this.registerSubscriptionChangeListener(lifecycleId, null, uidSite)
      if (!this.isCurrentLifecycle(lifecycleId)) {
        subscriptionListener.cleanup()
        return { status: 'skipped', reason: 'destroyed' }
      }
      this.cleanupSubscriptionListener = subscriptionListener.cleanup
      const reconcileResult = await subscriptionListener.reconcile()
      if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
      return reconcileResult.status === 'failed' ? reconcileResult : { status: 'success' }
    }

    this.dt = dt
    const domain = window.location.hostname
    let autoPrompt = false
    let dbSubscriptionStatus = null
    try {
      const { status, subscribedOrigin } = await this.fetchSubscribeStatus({ dt, domain })
      dbSubscriptionStatus = { status, subscribedOrigin }
      autoPrompt = status === false || (status === true && this.toHostname(subscribedOrigin) === domain)
    } catch (error) {
      console.warn('[OneSignal] subscription status failed', error)
    }

    if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
    await this.initSdk({ autoPrompt }, lifecycleId)
    if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }

    const subscriptionListener = await this.registerSubscriptionChangeListener(lifecycleId, dbSubscriptionStatus, dt)
    if (!this.isCurrentLifecycle(lifecycleId)) {
      subscriptionListener.cleanup()
      return { status: 'skipped', reason: 'destroyed' }
    }
    this.cleanupSubscriptionListener = subscriptionListener.cleanup

    const reconcileResult = await subscriptionListener.reconcile()
    if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
    return reconcileResult.status === 'failed' ? reconcileResult : { status: 'success' }
  }

  async initSdk({ autoPrompt }, lifecycleId) {
    if (this.isInitialized) return
    if (this.sdkInitPromise) {
      await this.sdkInitPromise
      return
    }

    const sdkInitPromise = (async () => {
      const appId = await this.fetchAppId()
      if (!this.isCurrentLifecycle(lifecycleId)) return
      const translate = (key) => this.app.$i18n?.t?.(key) || key
      await this.oneSignal.init({
        appId,
        notifyButton: { enable: false },
        serviceWorkerParam: { scope: '/push/onesignal/' },
        serviceWorkerPath: 'push/onesignal/OneSignalSDKWorker.js',
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: 'push',
                autoPrompt,
                text: {
                  actionMessage: translate('onesignal.action_message'),
                  acceptButton: translate('onesignal.accept_button'),
                  cancelButton: translate('onesignal.cancel_button'),
                },
              },
            ],
          },
        },
        autoPrompt,
      })

      const sdk = await this.oneSignal.getInitializedSDK()
      if (!sdk) throw new Error('OneSignal SDK unavailable after initialization')
      this.isInitialized = true
    })()
    this.sdkInitPromise = sdkInitPromise
    try {
      await sdkInitPromise
    } finally {
      if (this.sdkInitPromise === sdkInitPromise) this.sdkInitPromise = null
    }
  }

  async fetchAppId() {
    try {
      const domain = window.location.hostname
      if (domain === new URL(this.config.clientUrl).hostname) return this.config.oneSignal

      const url = `https://storage.googleapis.com/${this.config.bucketName}/site_resources/${this.config.clientId}/${domain}/onesignal.json`
      const response = await fetch(url)
      if (!response.ok) return this.config.oneSignal
      const data = await response.json()
      return data?.onesignal || this.config.oneSignal
    } catch {
      return this.config.oneSignal
    }
  }

  async fetchSubscribeStatus({ dt, domain }) {
    return await this.app.$request.send(
      subscribeApi.getStatus({
        deviceTokenId: dt,
        domain,
      })
    )
  }

  async registerSubscriptionChangeListener(lifecycleId, initialDbSubscriptionStatus, externalId) {
    const sdk = await this.oneSignal.getInitializedSDK()
    if (!this.isCurrentLifecycle(lifecycleId)) {
      return {
        cleanup: () => {},
        reconcile: async () => ({ status: 'skipped', reason: 'destroyed' }),
      }
    }
    const pushSubscription = sdk?.User?.PushSubscription
    if (!pushSubscription) throw new Error('PushSubscription API unavailable')
    const dt = this.dt
    let dbSubscriptionStatus = initialDbSubscriptionStatus
    let syncPromise = null
    let identityBound = false
    let isSubscribed = pushSubscription.optedIn && !!pushSubscription.id

    const syncSubscription = async () => {
      if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped' }
      if (syncPromise) return await syncPromise

      const request = (async () => {
        let result = { status: 'success' }
        if (dt) {
          try {
            result = await this.updateSubscription({ dt, dbSubscriptionStatus })
          } catch (error) {
            console.warn('[OneSignal] subscription update failed', error)
            result = { status: 'failed', reason: 'subscription_update_failed' }
          }
          if (result.status === 'success' && this.isCurrentLifecycle(lifecycleId)) {
            dbSubscriptionStatus = { status: true, subscribedOrigin: window.location.hostname }
          }
        }

        if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
        if (!isSubscribed) return result
        if (!identityBound) {
          const isLoggedIn = await this.oneSignal.login(externalId)
          if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
          if (!isLoggedIn) return { status: 'failed', reason: 'identity_login_failed' }
          if (dt) await sdk.User.addTag('Device_Token', dt)
          if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped', reason: 'destroyed' }
          identityBound = true
        }
        return result
      })().catch((error) => {
        console.warn('[OneSignal] identity binding failed', error)
        return { status: 'failed', reason: 'identity_binding_failed' }
      })
      syncPromise = request
      this.pendingSyncPromise = request
      try {
        return await request
      } finally {
        if (syncPromise === request) syncPromise = null
        if (this.pendingSyncPromise === request) this.pendingSyncPromise = null
      }
    }

    const handleChange = async (event) => {
      if (!this.isCurrentLifecycle(lifecycleId)) return { status: 'skipped' }
      const subscriptionId = event?.current?.id
      isSubscribed = !!(event?.current?.optedIn && subscriptionId)
      const nowSubscribed = isSubscribed && (event?.previous?.optedIn !== true || subscriptionId !== event?.previous?.id)
      if (!nowSubscribed) return { status: 'skipped' }
      return await syncSubscription()
    }

    pushSubscription.addEventListener('change', handleChange)
    return {
      cleanup: () => pushSubscription.removeEventListener('change', handleChange),
      reconcile: async () => {
        const subscriptionId = pushSubscription.id
        isSubscribed = !!(pushSubscription.optedIn && subscriptionId)
        if (!isSubscribed) return { status: 'skipped' }
        return await syncSubscription()
      },
    }
  }

  async updateSubscription({ dt, dbSubscriptionStatus }) {
    const domain = window.location.hostname
    if (dbSubscriptionStatus?.status === true && this.toHostname(dbSubscriptionStatus.subscribedOrigin) === domain) {
      return { status: 'success', reason: 'already_subscribed' }
    }
    const { success } = await this.app.$request.send(
      subscribeApi.update({
        deviceTokenId: dt,
        domain,
        action: 'subscribe',
      })
    )

    if (success !== true) return { status: 'failed', reason: 'subscription_update_failed' }
    return { status: 'success' }
  }

  // 後端欄位名為 origin，但契約未定：同時接受 hostname 與含 scheme 的 origin
  toHostname(value) {
    if (typeof value !== 'string' || !value) return null
    if (!value.includes('://')) return value.toLowerCase()
    try {
      return new URL(value).hostname
    } catch {
      return null
    }
  }

  isCurrentLifecycle(lifecycleId) {
    return this.lifecycleId === lifecycleId
  }

  removeSubscriptionListener() {
    if (!this.cleanupSubscriptionListener) return
    this.cleanupSubscriptionListener()
    this.cleanupSubscriptionListener = null
  }

  destroy() {
    this.lifecycleId += 1
    this.removeSubscriptionListener()
    this.dt = null
    this.activeSetupKey = null
  }

  async teardown() {
    const setupPromise = this.setupPromise
    const pendingSyncPromise = this.pendingSyncPromise
    const sdkInitPromise = this.sdkInitPromise
    this.destroy()
    if (this.teardownPromise) return await this.teardownPromise

    const teardownPromise = Promise.resolve()
      .then(async () => {
        if (setupPromise) await setupPromise
        if (pendingSyncPromise) await pendingSyncPromise
        if (sdkInitPromise) await sdkInitPromise
        if (this.isInitialized) await this.oneSignal.logout()
      })
      .catch((error) => {
        console.warn('[OneSignal] logout failed', error)
      })

    this.teardownPromise = teardownPromise
    try {
      await teardownPromise
    } finally {
      if (this.teardownPromise === teardownPromise) this.teardownPromise = null
    }
  }
}

export const useOneSignalSubscription = () => {
  const nuxtApp = useNuxtApp()
  if (!nuxtApp._oneSignalSubscription) {
    const { dtVerify } = useSite()
    const { uidSite } = useUserInfo()
    nuxtApp._oneSignalSubscription = new OneSignalSubscriptionService(nuxtApp, useRuntimeConfig().public, { dtVerify, uidSite })
  }
  return nuxtApp._oneSignalSubscription
}
